// Bileli · app/router — the SESSION NAV-STACK (pure, zero Teact/global imports — unit-testable).
// why: the old history model kept ONE section record whose hash-keyed remount was a silent REPLACE, so
// browser Back from any home tab exited straight to chat (never Каталог → Главная) and mode switches
// destroyed history. This stack records every visited screen; BileliHistoryHandler renders one
// useHistoryBack record per entry (bottom-first), so Back walks the real visit order — the "react-router
// feel" — while useHistoryBack stays the single window.history owner. The stack is per-tab module state,
// deliberately NOT in Telegram's multitab-shared `global` and NOT persisted (a reload seeds a fresh
// parent chain from the URL, like any SPA).

import type { BileliContext } from '../store/bileliContext.types';
import type { BileliRouteMode } from './routes';

export interface NavEntry {
  /** Unique, monotonic — the Teact key half + the idempotent pop address. */
  seq: number;
  mode: BileliRouteMode;
  /** Always a CONCRETE context (`{ kind: 'dashboard' }`), never undefined. */
  context: BileliContext;
  /** Frozen at push time — records never re-derive it (prevents pop/push storms during popstate). */
  hash: string;
  /** Scroll position of the page when this entry got covered by a push (restored on Back). */
  scrollTop?: number;
}

/** Depth cap — drop-oldest keeps Back useful without unbounded browser history. */
export const MAX_DEPTH = 8;
/** Chat detours truncate to this many entries (matches the proven max re-push burst of the old model). */
export const TAIL_ON_CHAT = 4;

/** Append an entry. No-op ONLY on a consecutive duplicate hash (Главная→Каталог→Главная = 3 entries). */
export function push(entries: readonly NavEntry[], entry: NavEntry): readonly NavEntry[] {
  const top = entries[entries.length - 1];
  if (top && top.hash === entry.hash) return entries;
  const next = [...entries, entry];
  return next.length > MAX_DEPTH ? next.slice(next.length - MAX_DEPTH) : next;
}

/** Replace the top entry (module draft → lesson leaf: URL replace, Back must not return to the draft). */
export function replaceTop(entries: readonly NavEntry[], entry: NavEntry): readonly NavEntry[] {
  if (!entries.length) return [entry];
  return [...entries.slice(0, -1), entry];
}

/** Remove every entry with seq >= the given seq. IDEMPOTENT — a repeat pop (Safari double-popstate,
 * multi-back walks) is a no-op once the seq is gone. */
export function popBelow(entries: readonly NavEntry[], seq: number): readonly NavEntry[] {
  const idx = entries.findIndex((e) => e.seq >= seq);
  if (idx === -1) return entries;
  return entries.slice(0, idx);
}

/** Keep at most the LAST n entries (chat detour: history collapses but the recent trail survives). */
export function truncateTail(entries: readonly NavEntry[], n: number): readonly NavEntry[] {
  return entries.length > n ? entries.slice(entries.length - n) : entries;
}

/** Record the current scroll position on the top entry (called just before a push covers it). */
export function withScrollTop(entries: readonly NavEntry[], scrollTop: number | undefined): readonly NavEntry[] {
  if (scrollTop === undefined || !entries.length) return entries;
  const top = entries[entries.length - 1];
  return [...entries.slice(0, -1), { ...top, scrollTop }];
}

/**
 * Reload seed: build the ancestor chain for a deep-linked context by walking `parentOf` up to a root,
 * bottom-first (e.g. module-draft → [teach-home, editor, module, draft]). Capped + visited-guarded so a
 * mis-declared parent cycle can never hang the boot.
 */
export function seedChain(
  context: BileliContext,
  mode: BileliRouteMode,
  parentOf: (ctx: BileliContext, mode: BileliRouteMode) => BileliContext | undefined,
  buildEntry: (ctx: BileliContext, mode: BileliRouteMode) => NavEntry,
): readonly NavEntry[] {
  const chain: BileliContext[] = [context];
  const seen = new Set<string>([JSON.stringify(context)]);
  for (let hops = 0; hops < 4; hops++) {
    const parent = parentOf(chain[0], mode);
    if (!parent) break;
    const key = JSON.stringify(parent);
    if (seen.has(key)) break; // cycle guard
    seen.add(key);
    chain.unshift(parent);
  }
  return chain.map((ctx) => buildEntry(ctx, mode));
}
